import { type BoxRect, type GeometryResolver } from '@databases/highlights';
import {
  getTableV2GeometryTarget,
  type TableV2GeometryTarget
} from '@databases/view-layout-table-v2/dom';

/**
 * Projects logical V2 indices through the mounted renderer's numeric geometry.
 * No cell DOM queries, historical cache, header observer or V1 pinning policy.
 * The renderer supplies the body origin once per committed layout change.
 */
export class TableV2GeometryResolver implements GeometryResolver {
  private readonly getContainer: () => HTMLElement | null;

  constructor(getContainer: () => HTMLElement | null) {
    this.getContainer = getContainer;
  }

  private getTarget(): TableV2GeometryTarget | undefined {
    const container = this.getContainer();
    const target = container ? getTableV2GeometryTarget(container) : undefined;

    return target?.available ? target : undefined;
  }

  private translate(
    target: TableV2GeometryTarget | undefined,
    rect: BoxRect | undefined
  ): BoxRect | null {
    return target && rect
      ? { ...rect, x: rect.x + target.origin.left, y: rect.y + target.origin.top }
      : null;
  }

  getScrollContainer(): HTMLElement | null {
    return this.getTarget()?.scrollContainer ?? null;
  }

  getCellBounds(rowIndex: number, colIndex: number): BoxRect | null {
    const target = this.getTarget();
    const row = target?.model.rows[rowIndex];
    const col = target?.model.columns[colIndex];

    return this.translate(
      target,
      row && col
        ? target?.geometry.getCellRect({ entryId: row.entryId, fieldId: col.fieldId })
        : undefined
    );
  }

  getRowBounds(rowIndex: number): BoxRect | null {
    const target = this.getTarget();
    const row = target?.model.rows[rowIndex];

    return this.translate(
      target,
      row ? target?.geometry.getRowRect(row.entryId) : undefined
    );
  }

  getColumnBounds(colIndex: number): BoxRect | null {
    const target = this.getTarget();
    const col = target?.model.columns[colIndex];

    return this.translate(
      target,
      col ? target?.geometry.getColumnRect(col.fieldId) : undefined
    );
  }

  getContainerBounds(): BoxRect | null {
    const target = this.getTarget();

    return this.translate(
      target,
      target
        ? { x: 0, y: 0, width: target.totalWidth, height: target.totalHeight }
        : undefined
    );
  }
}
